import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { Modal, Spinner } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import Navbar from '../../components/navbar/navbar';
import apinode from '../../services/apinode';
import { toast } from 'react-toastify';

const ImportExcel = () => {
    const [data, setData] = useState([]);
    const [filteredPayload, setFilteredPayload] = useState([]);
    const [stockUpdatePayload, setStockUpdatePayload] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [modalType, setModalType] = useState('');
    const [results, setResults] = useState(null); // { success: [], errors: [] }
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [dragOver, setDragOver] = useState(false);
    const [fileName, setFileName] = useState('');

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    };

    // Converte uma célula (número OU texto em formato BR "1.234,56") para float.
    // - Se já vier como number (caso comum no XLSX), usa direto.
    // - Se vier como texto, remove separador de milhar e troca vírgula por ponto.
    // - Retorna NaN quando estiver vazio/invalido (tratado depois no filtro).
    const parseBRNumber = (v) => {
        if (v === null || v === undefined || v === '') return NaN;
        if (typeof v === 'number') return v;
        return parseFloat(String(v).replace(/\./g, '').replace(',', '.'));
    };

    const processFile = (file) => {
        if (!file) return;
        setFileName(file.name);
        const reader = new FileReader();
        reader.onload = (event) => {
            const binaryStr = event.target.result;
            const workbook = XLSX.read(binaryStr, { type: 'binary' });
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];
            const sheetData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            const filteredData = sheetData.filter(row => {
                const cellValue = String(row[0]);
                return !cellValue.startsWith('Totais');
            });
            setData(filteredData);
            setResults(null);
        };
        reader.readAsBinaryString(file);
    };

    const handleFileUpload = (e) => processFile(e.target.files[0]);

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        processFile(e.dataTransfer.files[0]);
    };

    const prepareDataForSending = () => {
        const payload = data.slice(1).map(row => {
            const entradaQtd = parseBRNumber(row[3]);
            const quantidade = parseBRNumber(row[3]);
            const valorDinheiro = parseBRNumber(row[4]);
            const unitario = valorDinheiro / entradaQtd;
            return {
                codigo: row[0],
                qtd: row[3],
                entradas: quantidade,
                valorEntradas: valorDinheiro,
                valorUni: unitario
            };
        });
        const filtered = payload.filter(item =>
            !isNaN(item.entradas) &&
            !isNaN(item.valorEntradas) &&
            item.valorUni > 0
        );
        setFilteredPayload(filtered);
        setResults(null);
        setModalType('sendData');
        setShowModal(true);
    };

    const prepareStockUpdateData = () => {
        const payload = data.slice(1).map(row => {
            const quantidade = parseBRNumber(row[3]);
            const valorDinheiro = parseBRNumber(row[4]);
            return {
                codigo: row[0],
                entradas: quantidade,
                valorEntradas: valorDinheiro
            };
        });
        const filtered = payload.filter(item =>
            !isNaN(item.entradas) &&
            !isNaN(item.valorEntradas) &&
            item.entradas > 0
        );
        setStockUpdatePayload(filtered);
        setResults(null);
        setModalType('updateStock');
        setShowModal(true);
    };

    const handleSendData = async () => {
        setLoading(true);
        setProgress({ current: 0, total: filteredPayload.length });
        const success = [];
        const errors = [];

        for (let i = 0; i < filteredPayload.length; i++) {
            const item = filteredPayload[i];
            setProgress({ current: i + 1, total: filteredPayload.length });
            try {
                await apinode.put(`/estoque/saldoestoque/${localStorage.idconta}`, [item]);
                success.push(item);
            } catch (error) {
                const msg = error?.response?.data?.error || error.message || 'Erro desconhecido';
                errors.push({ ...item, erro: msg });
            }
        }

        setLoading(false);
        setResults({ success, errors });

        if (errors.length === 0) {
            toast.success(`${success.length} SKU(s) atualizados com sucesso!`);
        } else if (success.length === 0) {
            toast.error(`Todos os ${errors.length} SKU(s) falharam.`);
        } else {
            toast.warning(`${success.length} OK · ${errors.length} com erro`);
        }
    };

    const handleUpdateStock = async () => {
        setLoading(true);
        setProgress({ current: 0, total: stockUpdatePayload.length });
        const success = [];
        const errors = [];

        for (let i = 0; i < stockUpdatePayload.length; i++) {
            const item = stockUpdatePayload[i];
            setProgress({ current: i + 1, total: stockUpdatePayload.length });
            try {
                // 1) Atualiza o estoque
                await apinode.put(`/estoque/atualizarestoque/${localStorage.idconta}`, [item]);

                // 2) Insere a tarefa no bot (reativar SKU) — remetente SISTEMA
                //    não dispara trigger do Firebird nem mensagem no grupo
                await apinode.post(`/tarefa-bot/${localStorage.idconta}`, {
                    sku: item.codigo,
                    quantidade: item.entradas,
                    remetente: 'SISTEMA',
                });

                success.push(item);
            } catch (error) {
                const msg = error?.response?.data?.error || error.message || 'Erro desconhecido';
                errors.push({ ...item, erro: msg });
            }
        }

        setLoading(false);
        setResults({ success, errors });

        if (errors.length === 0) {
            toast.success(`${success.length} SKU(s) atualizados com sucesso!`);
        } else if (success.length === 0) {
            toast.error(`Todos os ${errors.length} SKU(s) falharam.`);
        } else {
            toast.warning(`${success.length} OK · ${errors.length} com erro`);
        }
    };
    const handleModalConfirm = () => {
        if (modalType === 'sendData') handleSendData();
        else if (modalType === 'updateStock') handleUpdateStock();
    };

    const currentPayload = modalType === 'sendData' ? filteredPayload : stockUpdatePayload;
    const progressPct = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

    return (
        <>
            <Navbar />
            <div style={styles.page}>
                <div style={styles.container}>
                    {/* Header */}
                    <div style={styles.header}>
                        <div style={styles.headerIcon}>📊</div>
                        <div>
                            <h1 style={styles.title}>Importar Planilha</h1>
                            <p style={styles.subtitle}>Faça upload de um arquivo Excel (.xls / .xlsx) para atualizar custos ou estoque</p>
                        </div>
                    </div>

                    {/* Drop Zone */}
                    <label
                        style={{ ...styles.dropZone, ...(dragOver ? styles.dropZoneActive : {}) }}
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={handleDrop}
                    >
                        <input type="file" accept=".xls,.xlsx" onChange={handleFileUpload} style={{ display: 'none' }} />
                        {fileName ? (
                            <>
                                <div style={styles.dropIcon}>✅</div>
                                <p style={styles.dropLabel}>{fileName}</p>
                                <p style={styles.dropHint}>{data.length > 1 ? `${data.length - 1} linhas carregadas` : ''}</p>
                            </>
                        ) : (
                            <>
                                <div style={styles.dropIcon}>📂</div>
                                <p style={styles.dropLabel}>Arraste o arquivo aqui ou clique para selecionar</p>
                                <p style={styles.dropHint}>.xls · .xlsx</p>
                            </>
                        )}
                    </label>

                    {/* Action Buttons */}
                    {data.length > 0 && (
                        <div style={styles.actions}>
                            <button onClick={prepareDataForSending} style={styles.btnPrimary}>
                                💰 Atualizar Custo (Preço)
                            </button>
                            <button onClick={prepareStockUpdateData} style={styles.btnSecondary}>
                                📦 Atualizar Estoque
                            </button>
                        </div>
                    )}

                    {/* Preview Table */}
                    {data.length > 0 && (
                        <div style={styles.tableCard}>
                            <p style={styles.tableTitle}>Prévia — {data.length - 1} linhas</p>
                            <div style={styles.tableWrapper}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            {data[0].map((header, i) => (
                                                <th key={i} style={styles.th}>{header}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.slice(1).map((row, ri) => (
                                            <tr key={ri} style={ri % 2 === 0 ? styles.trEven : styles.trOdd}>
                                                {row.map((cell, ci) => (
                                                    <td key={ci} style={styles.td}>{cell}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            <Modal show={showModal} onHide={() => !loading && setShowModal(false)} size="lg" centered>
                <div style={styles.modalInner}>
                    <div style={styles.modalHeader}>
                        <span style={styles.modalTitle}>
                            {modalType === 'sendData' ? '💰 Confirmar Atualização de Custo' : '📦 Confirmar Atualização de Estoque'}
                        </span>
                        {!loading && (
                            <button style={styles.closeBtn} onClick={() => setShowModal(false)}>✕</button>
                        )}
                    </div>

                    <div style={styles.modalBody}>
                        {/* Loading State */}
                        {loading && (
                            <div style={styles.progressBlock}>
                                <div style={styles.progressBar}>
                                    <div style={{ ...styles.progressFill, width: `${progressPct}%` }} />
                                </div>
                                <p style={styles.progressLabel}>Processando {progress.current} de {progress.total} SKUs... {progressPct}%</p>
                            </div>
                        )}

                        {/* Results */}
                        {results && !loading && (
                            <>
                                <div style={styles.resultRow}>
                                    <div style={styles.badge('success')}>✅ {results.success.length} com sucesso</div>
                                    {results.errors.length > 0 && (
                                        <div style={styles.badge('error')}>❌ {results.errors.length} com erro</div>
                                    )}
                                </div>
                                {results.errors.length > 0 && (
                                    <div style={styles.errorBlock}>
                                        <p style={styles.errorTitle}>SKUs com erro:</p>
                                        {results.errors.map((item, i) => (
                                            <div key={i} style={styles.errorItem}>
                                                <span style={styles.errorSku}>{item.codigo}</span>
                                                <span style={styles.errorMsg}>{item.erro}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}

                        {/* Pending list (before sending) */}
                        {!loading && !results && (
                            <>
                                <p style={styles.confirmText}>
                                    {currentPayload.length} SKU(s) serão processados. Deseja continuar?
                                </p>
                                <div style={styles.skuList}>
                                    {currentPayload.map((item, i) => (
                                        <div key={i} style={styles.skuItem}>
                                            <span style={styles.skuCode}>{item.codigo}</span>
                                            {modalType === 'sendData'
                                                ? <span style={styles.skuVal}>{formatCurrency(item.valorUni)}</span>
                                                : <span style={styles.skuVal}>{item.entradas} un.</span>
                                            }
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>

                    <div style={styles.modalFooter}>
                        {!loading && !results && (
                            <>
                                <button style={styles.btnCancel} onClick={() => setShowModal(false)}>Cancelar</button>
                                <button style={styles.btnConfirm} onClick={handleModalConfirm}>
                                    {modalType === 'sendData' ? 'Confirmar Envio' : 'Confirmar Atualização'}
                                </button>
                            </>
                        )}
                        {results && !loading && (
                            <button style={styles.btnConfirm} onClick={() => setShowModal(false)}>Fechar</button>
                        )}
                        {loading && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#6b7280' }}>
                                <Spinner animation="border" size="sm" /> Aguarde...
                            </div>
                        )}
                    </div>
                </div>
            </Modal>
        </>
    );
};

const styles = {
    page: {
        minHeight: '100vh',
        background: '#f8fafc',
        paddingTop: 80,
        paddingBottom: 48,
    },
    container: {
        maxWidth: 960,
        margin: '0 auto',
        padding: '0 24px',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        marginBottom: 32,
    },
    headerIcon: {
        fontSize: 40,
        lineHeight: 1,
    },
    title: {
        margin: 0,
        fontSize: 26,
        fontWeight: 700,
        color: '#111827',
    },
    subtitle: {
        margin: 0,
        fontSize: 14,
        color: '#6b7280',
        marginTop: 4,
    },
    dropZone: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        border: '2px dashed #d1d5db',
        borderRadius: 12,
        padding: '40px 24px',
        background: '#fff',
        cursor: 'pointer',
        transition: 'border-color 0.2s, background 0.2s',
        textAlign: 'center',
        marginBottom: 24,
    },
    dropZoneActive: {
        borderColor: '#3b82f6',
        background: '#eff6ff',
    },
    dropIcon: {
        fontSize: 36,
    },
    dropLabel: {
        margin: 0,
        fontSize: 15,
        fontWeight: 600,
        color: '#374151',
    },
    dropHint: {
        margin: 0,
        fontSize: 12,
        color: '#9ca3af',
    },
    actions: {
        display: 'flex',
        gap: 12,
        marginBottom: 24,
        flexWrap: 'wrap',
    },
    btnPrimary: {
        background: '#2563eb',
        color: '#fff',
        border: 'none',
        borderRadius: 8,
        padding: '10px 20px',
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
    },
    btnSecondary: {
        background: '#fff',
        color: '#374151',
        border: '1.5px solid #d1d5db',
        borderRadius: 8,
        padding: '10px 20px',
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
    },
    tableCard: {
        background: '#fff',
        borderRadius: 12,
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
    },
    tableTitle: {
        margin: 0,
        padding: '12px 16px',
        fontSize: 13,
        fontWeight: 600,
        color: '#6b7280',
        borderBottom: '1px solid #f3f4f6',
        background: '#f9fafb',
    },
    tableWrapper: {
        overflowX: 'auto',
        maxHeight: 360,
        overflowY: 'auto',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 12,
    },
    th: {
        padding: '8px 12px',
        textAlign: 'left',
        fontWeight: 600,
        color: '#374151',
        background: '#f9fafb',
        borderBottom: '1px solid #e5e7eb',
        whiteSpace: 'nowrap',
        position: 'sticky',
        top: 0,
        zIndex: 1,
    },
    td: {
        padding: '7px 12px',
        color: '#4b5563',
        borderBottom: '1px solid #f3f4f6',
        whiteSpace: 'nowrap',
    },
    trEven: { background: '#fff' },
    trOdd: { background: '#fafafa' },

    // Modal
    modalInner: {
        borderRadius: 12,
        overflow: 'hidden',
        background: '#fff',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '18px 24px',
        borderBottom: '1px solid #e5e7eb',
    },
    modalTitle: {
        fontSize: 16,
        fontWeight: 700,
        color: '#111827',
    },
    closeBtn: {
        background: 'none',
        border: 'none',
        fontSize: 16,
        color: '#9ca3af',
        cursor: 'pointer',
    },
    modalBody: {
        padding: '20px 24px',
        maxHeight: 420,
        overflowY: 'auto',
    },
    confirmText: {
        margin: '0 0 16px',
        fontSize: 14,
        color: '#374151',
    },
    skuList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
    },
    skuItem: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 12px',
        background: '#f9fafb',
        borderRadius: 8,
        fontSize: 13,
    },
    skuCode: {
        fontWeight: 600,
        color: '#111827',
        fontFamily: 'monospace',
    },
    skuVal: {
        color: '#2563eb',
        fontWeight: 500,
    },
    progressBlock: {
        marginBottom: 16,
    },
    progressBar: {
        height: 8,
        background: '#e5e7eb',
        borderRadius: 99,
        overflow: 'hidden',
        marginBottom: 8,
    },
    progressFill: {
        height: '100%',
        background: '#2563eb',
        borderRadius: 99,
        transition: 'width 0.3s ease',
    },
    progressLabel: {
        fontSize: 13,
        color: '#6b7280',
        margin: 0,
    },
    resultRow: {
        display: 'flex',
        gap: 10,
        marginBottom: 16,
        flexWrap: 'wrap',
    },
    badge: (type) => ({
        padding: '6px 14px',
        borderRadius: 99,
        fontSize: 13,
        fontWeight: 600,
        background: type === 'success' ? '#dcfce7' : '#fee2e2',
        color: type === 'success' ? '#15803d' : '#dc2626',
    }),
    errorBlock: {
        background: '#fff8f8',
        border: '1px solid #fecaca',
        borderRadius: 8,
        padding: 12,
    },
    errorTitle: {
        margin: '0 0 8px',
        fontSize: 13,
        fontWeight: 600,
        color: '#dc2626',
    },
    errorItem: {
        display: 'flex',
        gap: 12,
        padding: '6px 0',
        borderBottom: '1px solid #fee2e2',
        fontSize: 12,
        alignItems: 'center',
    },
    errorSku: {
        fontFamily: 'monospace',
        fontWeight: 700,
        color: '#111827',
        minWidth: 100,
    },
    errorMsg: {
        color: '#b91c1c',
    },
    modalFooter: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 10,
        padding: '14px 24px',
        borderTop: '1px solid #e5e7eb',
    },
    btnCancel: {
        background: '#fff',
        color: '#374151',
        border: '1.5px solid #d1d5db',
        borderRadius: 8,
        padding: '9px 18px',
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
    },
    btnConfirm: {
        background: '#2563eb',
        color: '#fff',
        border: 'none',
        borderRadius: 8,
        padding: '9px 20px',
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
    },
};

export default ImportExcel;